import { useEffect, useRef, useState } from 'react';
import { useNavigation } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { ring, ringVisible, spinner } from './clickBusyCursor.css';

/**
 * ClickBusyCursor
 *
 * Uniform, zero-delay "something is happening" feedback. The instant any
 * interactive element is clicked, a small branded spinner appears beside the
 * pointer — this fires on the raw `pointerdown` event, synchronously, BEFORE
 * React, the router, or react-query do any work, so it lands in the gap between
 * the click and the view actually changing (the moment that otherwise feels
 * laggy). The spinner follows the cursor while busy.
 *
 * It then holds through whatever async work the click triggered (a route
 * transition or any in-flight fetch) and disappears the moment that work
 * settles. For clicks that do no async work, a brief flash already conveys
 * "registered", so it clears after a short grace window.
 *
 * Mounted once in the root layout; covers every click in the app with no
 * per-element wiring. Unlike the OS busy cursor, this is on-brand, animated, and
 * identical on every platform.
 */
export function ClickBusyCursor() {
  const navigation = useNavigation();
  const fetching = useIsFetching();
  const workBusy = navigation.state !== 'idle' || fetching > 0;
  const ringRef = useRef<HTMLDivElement>(null);
  const visible = useClickBusyCursor({ workBusy, ringRef });
  return (
    <div ref={ringRef} aria-hidden="true" className={visible ? `${ring} ${ringVisible}` : ring}>
      <span className={spinner} />
    </div>
  );
}

// Action-ish targets only: things that navigate or mutate, not text fields
// (focusing an input isn't "changing the view", and a spinner there is odd).
const INTERACTIVE_SELECTOR =
  'a[href], button, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], summary, [data-busy-cursor]';

// Instant actions: flash the spinner, then clear if no async work materialized.
const GRACE_MS = 220;
// Backstop so the spinner can never get stuck if `workBusy` somehow never clears.
const SAFETY_MS = 15_000;
// How far the spinner trails the pointer hotspot (top-left of the arrow).
const OFFSET_PX = 14;

/**
 * The behavior, decoupled from the router/query hooks so it can be unit-tested
 * with just a `busy` flag, a ring element, and synthetic pointer events.
 * Returns whether the spinner should currently be visible; positions the ring
 * imperatively (no re-render per pointer move).
 */
export function useClickBusyCursor({
  workBusy,
  ringRef,
}: {
  workBusy: boolean;
  ringRef: { current: HTMLDivElement | null };
}) {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  visibleRef.current = visible;
  // A click started a cursor cycle and we're still waiting on / running its work.
  const armedRef = useRef(false);
  const workBusyRef = useRef(workBusy);
  workBusyRef.current = workBusy;
  const graceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const safetyRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Shared so the workBusy effect can end a cycle the listener started.
  const revertRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const moveTo = (x: number, y: number) => {
      const el = ringRef.current;
      if (el) el.style.transform = `translate3d(${x + OFFSET_PX}px, ${y + OFFSET_PX}px, 0)`;
    };
    const clearTimers = () => {
      if (graceRef.current) clearTimeout(graceRef.current);
      if (safetyRef.current) clearTimeout(safetyRef.current);
      graceRef.current = undefined;
      safetyRef.current = undefined;
    };
    const revert = () => {
      armedRef.current = false;
      clearTimers();
      setVisible(false);
    };
    revertRef.current = revert;

    const onPointerDown = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Element) || !target.closest(INTERACTIVE_SELECTOR)) return;

      // The whole point: this runs before any React work, so the click is
      // acknowledged immediately rather than after the view-change latency.
      if (e instanceof MouseEvent) moveTo(e.clientX, e.clientY);
      setVisible(true);
      armedRef.current = true;
      clearTimers();
      graceRef.current = setTimeout(() => {
        // No async work showed up — the flash did its job; let the spinner go.
        if (!workBusyRef.current) revert();
      }, GRACE_MS);
      safetyRef.current = setTimeout(revert, SAFETY_MS);
    };

    const onPointerMove = (e: Event) => {
      if (!visibleRef.current) return;
      if (e instanceof MouseEvent) moveTo(e.clientX, e.clientY);
    };

    // Capture phase so we still see clicks on handlers that stop propagation.
    document.addEventListener('pointerdown', onPointerDown, { capture: true });
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, { capture: true });
      document.removeEventListener('pointermove', onPointerMove);
      revert();
    };
  }, [ringRef]);

  // Bridge the instant click feedback to the real work it kicked off: hold the
  // spinner through genuine async work, then drop it the instant that work ends.
  useEffect(() => {
    if (!armedRef.current) return;
    if (workBusy) {
      // Real work is underway — cancel the instant-action grace revert.
      if (graceRef.current) clearTimeout(graceRef.current);
      graceRef.current = undefined;
    } else {
      revertRef.current?.();
    }
  }, [workBusy]);

  return visible;
}
